import React, { useMemo, useState, useEffect, useCallback } from "react";
import "./attendanceCal.css";
import Layout from "../../../Layout/Layout";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import ItemServiceV2 from "../../../services/human_resource_appCommonService";
import { addTaskNr } from "../../../redux/feature/hr_app/hrSlice";
import HRConstants from "../../../common/HRConstants";
import { useNavigate } from "react-router-dom";

const WEEKDAYS_FULL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
function toKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function parseDateOnly(value) {
  if (!value) return null;
  const datePart = String(value).split("T")[0];
  const [y, m, d] = datePart.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function isAdminUser(userData) {
  if (!userData) return false;
  const hasAdminRole = userData.roles?.some((r) => r.roleId === "ADMIN");
  const hasAdminJobRole = userData.jobRole === "Admin";
  return Boolean(hasAdminRole || hasAdminJobRole);
}
function eachDateInRange(start, end) {
  const dates = [];
  let cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  if (cur > last) return dates;
  while (cur <= last) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}
function buildTaskDateMap(tasks) {
  const map = {};
  (tasks || []).forEach((task) => {
    const start = parseDateOnly(task.startDate);
    const end = parseDateOnly(task.endDate || task.startDate);
    if (!start || !end) return;
    eachDateInRange(start, end).forEach((date) => {
      const key = toKey(date);
      if (!map[key]) map[key] = [];
      map[key].push(task);
    });
  });
  return map;
}
export default function TaskCal({ onSaveEntry }) {
  const today = useMemo(() => new Date(), []);
  const todayStart = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), today.getDate()),
    [today],
  );
  const userData = useSelector((state) => state?.user?.data);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  localStorage.setItem("menu", "Task Manage");
  localStorage.setItem("submenu", "Task List");

  const detailedPage = (taskNr) => {
    dispatch(addTaskNr(taskNr));
    navigate(HRConstants.HR_TASK_DETAILS);
  };
  const isAdmin = useMemo(() => isAdminUser(userData), [userData]);
  const loggedInEmployeeCode = userData?.customerCode || userData?.userId || "";
  const loggedInEmployeeName =
    userData?.employeeName || userData?.organisationName || "Employee";

  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(
    new Date(today.getFullYear(), today.getMonth(), today.getDate()),
  );
  const [selected, setSelected] = useState(cursor);
  const [employeeList, setEmployeeList] = useState([]);
  const [selectedEmployeeCode, setSelectedEmployeeCode] = useState("");

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [listModalDate, setListModalDate] = useState(null);

  const monthLabel = `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;

  const headerDateLabel = useMemo(() => {
    const opts = {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    };
    return selected.toLocaleDateString("en-US", opts);
  }, [selected]);

  const monthGrid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay();
    const gridStart = new Date(year, month, 1 - startOffset);

    const weeks = [];
    let day = new Date(gridStart);
    for (let w = 0; w < 6; w++) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        week.push(new Date(day));
        day.setDate(day.getDate() + 1);
      }
      weeks.push(week);
    }
    return weeks;
  }, [cursor]);

  const weekGrid = useMemo(() => {
    const start = new Date(selected);
    start.setDate(start.getDate() - start.getDay());
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [selected]);

  const miniGrid = monthGrid;

  // TRUE when admin has NOT picked a specific employee -> show everyone's tasks, grouped
  const isMultiEmployeeView = isAdmin && !selectedEmployeeCode;

  function isPastDay(date) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return d < todayStart;
  }
  // eslint-disable-next-line
  function canEditDate(date) {
    return isAdmin || !isPastDay(date);
  }
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      const params = {
        month: cursor.getMonth() + 1,
        year: cursor.getFullYear(),
      };
      await ItemServiceV2.getAllTask(params).then((item) => {
        if (item?.status === 200) {
          const all = item?.data || [];
          const uniqueMap = new Map();
          all.forEach((rec) => {
            if (rec.employeeCode && !uniqueMap.has(rec.employeeCode)) {
              uniqueMap.set(
                rec.employeeCode,
                rec.employeeName || rec.employeeCode,
              );
            }
          });
          setEmployeeList(
            Array.from(uniqueMap, ([code, name]) => ({ code, name })),
          );
        } else {
          toast.error(item?.message || "Unable to load employee list", {
            duration: 4000,
            position: "top-right",
          });
        }
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError("");
    const month = cursor.getMonth() + 1;
    const year = cursor.getFullYear();
    const params = { month, year };
    if (isAdmin && selectedEmployeeCode)
      params.employeeCode = selectedEmployeeCode;
    if (!isAdmin) params.employeeCode = loggedInEmployeeCode;

    const callApi = isAdmin
      ? ItemServiceV2.getAllTask(params)
      : ItemServiceV2.getTask(params);

    await callApi
      .then((item) => {
        if (item?.status === 200) {
          setTasks(item?.data || []);
        } else {
          setError(item?.message || "Unable to load task data");
          toast.error(item?.message || "Unable to load task data", {
            duration: 4000,
            position: "top-right",
          });
        }
      })
      .catch(() => {
        setError("Unable to load task data");
      });
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, isAdmin, selectedEmployeeCode, loggedInEmployeeCode]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);
  const taskDateMap = useMemo(() => buildTaskDateMap(tasks), [tasks]);
  const stats = useMemo(() => {
    const counts = { total: 0, ongoing: 0, upcoming: 0, overdue: 0 };
    Object.keys(taskDateMap).forEach((key) => {
      const d = new Date(key + "T00:00:00");
      if (
        d.getMonth() !== cursor.getMonth() ||
        d.getFullYear() !== cursor.getFullYear()
      )
        return;
      counts.total += 1;
    });
    tasks.forEach((t) => {
      const start = parseDateOnly(t.startDate);
      const end = parseDateOnly(t.endDate || t.startDate);
      if (!start || !end) return;
      if (todayStart >= start && todayStart <= end) counts.ongoing += 1;
      else if (todayStart < start) counts.upcoming += 1;
      else if (todayStart > end) counts.overdue += 1;
    });
    return counts;
  }, [taskDateMap, tasks, cursor, todayStart]);
  function entryFor(date) {
    return taskDateMap[toKey(date)] || null;
  }

  function goToday() {
    const t = new Date();
    setCursor(new Date(t.getFullYear(), t.getMonth(), t.getDate()));
    setSelected(t);
  }

  function stepMonth(delta) {
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));
  }

  function stepDay(delta) {
    const d = new Date(selected);
    d.setDate(d.getDate() + delta);
    setSelected(d);
    setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
  }

  function stepWeek(delta) {
    const d = new Date(selected);
    d.setDate(d.getDate() + delta * 7);
    setSelected(d);
    setCursor(new Date(d.getFullYear(), d.getMonth(), 1));
  }

  function handlePrev() {
    if (view === "month") stepMonth(-1);
    else if (view === "week") stepWeek(-1);
    else stepDay(-1);
  }

  function handleNext() {
    if (view === "month") stepMonth(1);
    else if (view === "week") stepWeek(1);
    else stepDay(1);
  }

  function openListModal(date) {
    setSelected(date);
    setListModalDate(date);
  }

  function closeListModal() {
    setListModalDate(null);
  }

  function openCell(date) {
    openListModal(date);
  }

  // Esc to close
  useEffect(() => {
    if (!listModalDate) return;
    function onKey(e) {
      if (e.key === "Escape") closeListModal();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [listModalDate]);
  function renderTaskChip(task, showEmployee) {
    if (!task) return null;

    return (
      <span
        className="atc-chip"
        style={{
          color: "var(--present)",
          background: "color-mix(in srgb, var(--present) 14%, transparent)",
          cursor: "pointer",
        }}
        title={`${task.taskNr} • ${task.apps || ""} (${task.startDate} → ${task.endDate})`}
        onClick={(e) => {
          e.stopPropagation();
          detailedPage(task.taskNr);
        }}
      >
        <span className="atc-dot" style={{ background: "var(--present)" }} />#
        {task.taskNr}
        {showEmployee
          ? ` • ${task.employeeName || task.employeeCode || ""}`
          : ""}
      </span>
    );
  }

  function renderCellContent(entry, date, max = 2) {
    if (!entry || !entry.length) return null;
    const visible = entry.slice(0, max);
    const remaining = entry.length - visible.length;
    return (
      <>
        {visible.map((t, idx) => (
          <span key={`${t.taskNr}-${idx}`}>
            {renderTaskChip(t, isMultiEmployeeView)}
          </span>
        ))}
        {remaining > 0 && (
          <span
            className="atc-weekend-label atc-more-link"
            onClick={(ev) => {
              ev.stopPropagation();
              openListModal(date);
            }}
          >
            +{remaining} more
          </span>
        )}
      </>
    );
  }

  return (
    <div className="atc">
      <Layout />
      <header className="atc-topbar">
        <div className="atc-brand">
          <span className="atc-brand-mark" aria-hidden="true">
            T
          </span>
          <div>
            <h1>Task</h1>
            <p className="atc-brand-sub">
              {isAdmin
                ? selectedEmployeeCode
                  ? employeeList.find((e) => e.code === selectedEmployeeCode)
                      ?.name || selectedEmployeeCode
                  : "All Employees"
                : loggedInEmployeeName}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {isAdmin && (
            <select
              className="atc-btn atc-btn-ghost"
              value={selectedEmployeeCode}
              onChange={(e) => setSelectedEmployeeCode(e.target.value)}
              aria-label="Select employee"
            >
              <option value="">All Employees</option>
              {employeeList.map((emp) => (
                <option key={emp.code} value={emp.code}>
                  {emp.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </header>

      <div className="atc-body">
        <aside className="atc-sidebar">
          <div className="atc-card atc-mini">
            <div className="atc-mini-head">
              <span>{monthLabel}</span>
              <div className="atc-mini-nav">
                <button
                  aria-label="Previous month"
                  onClick={() => stepMonth(-1)}
                >
                  ‹
                </button>
                <button aria-label="Next month" onClick={() => stepMonth(1)}>
                  ›
                </button>
              </div>
            </div>
            <div className="atc-mini-dow">
              {WEEKDAYS_FULL.map((d) => (
                <span key={d}>{d[0]}</span>
              ))}
            </div>
            <div className="atc-mini-grid">
              {miniGrid.flat().map((date, i) => {
                const inMonth = date.getMonth() === cursor.getMonth();
                const entry = entryFor(date);
                const isToday = isSameDay(date, today);
                const isSelected = isSameDay(date, selected);
                const hasTask = entry && entry.length > 0;
                return (
                  <button
                    key={i}
                    className={[
                      "atc-mini-day",
                      !inMonth && "is-outside",
                      isToday && "is-today",
                      isSelected && "is-selected",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => openCell(date)}
                  >
                    {date.getDate()}
                    {hasTask && (
                      <span
                        className="atc-mini-dot"
                        style={{ background: "var(--present)" }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="atc-card atc-stats">
            <h2>{monthLabel} Summary</h2>
            <div className="atc-stat-grid">
              <div className="atc-stat">
                <span
                  className="atc-stat-num"
                  style={{ color: "var(--present)" }}
                >
                  {stats.total}
                </span>
                <span className="atc-stat-label">Task Days</span>
              </div>
              <div className="atc-stat">
                <span className="atc-stat-num" style={{ color: "var(--late)" }}>
                  {stats.ongoing}
                </span>
                <span className="atc-stat-label">Ongoing</span>
              </div>
              <div className="atc-stat">
                <span
                  className="atc-stat-num"
                  style={{ color: "var(--leave)" }}
                >
                  {stats.upcoming}
                </span>
                <span className="atc-stat-label">Upcoming</span>
              </div>
              <div className="atc-stat">
                <span
                  className="atc-stat-num"
                  style={{ color: "var(--absent)" }}
                >
                  {stats.overdue}
                </span>
                <span className="atc-stat-label">Overdue</span>
              </div>
            </div>
          </div>
        </aside>

        <main className="atc-main">
          <div className="atc-toolbar">
            <div className="atc-toolbar-left">
              <button
                className="atc-icon-btn"
                aria-label="Previous"
                onClick={handlePrev}
              >
                ‹
              </button>
              <button className="atc-btn atc-btn-ghost" onClick={goToday}>
                Today
              </button>
              <button
                className="atc-icon-btn"
                aria-label="Next"
                onClick={handleNext}
              >
                ›
              </button>
              <h2 className="atc-toolbar-title">{headerDateLabel}</h2>
            </div>
            <div className="atc-view-switch" role="tablist">
              {["day", "week", "month"].map((v) => (
                <button
                  key={v}
                  role="tab"
                  aria-selected={view === v}
                  className={"atc-view-btn" + (view === v ? " is-active" : "")}
                  onClick={() => setView(v)}
                >
                  {v[0].toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="atc-view-area">
            {loading && (
              <p style={{ padding: "16px 20px", color: "var(--text-muted)" }}>
                Loading tasks…
              </p>
            )}
            {error && (
              <p style={{ padding: "16px 20px", color: "var(--absent)" }}>
                {error}
              </p>
            )}

            {!loading && view === "month" && (
              <>
                <div className="atc-grid-head">
                  {WEEKDAYS_FULL.map((d) => (
                    <div key={d}>{d}</div>
                  ))}
                </div>
                <div className="atc-month-grid">
                  {monthGrid.flat().map((date, i) => {
                    const inMonth = date.getMonth() === cursor.getMonth();
                    const entry = entryFor(date);
                    const isWeekend =
                      date.getDay() === 0 || date.getDay() === 6;
                    const isToday = isSameDay(date, today);
                    return (
                      <button
                        key={i}
                        className={[
                          "atc-cell",
                          !inMonth && "is-outside",
                          isWeekend && "is-weekend",
                          isToday && "is-today",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => openCell(date)}
                      >
                        <span className="atc-cell-num">{date.getDate()}</span>
                        {renderCellContent(entry, date)}
                        {isWeekend && !entry && (
                          <span className="atc-weekend-label">Weekend</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {!loading && view === "week" && (
              <div className="atc-week-grid">
                {weekGrid.map((date, i) => {
                  const entry = entryFor(date);
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  const isToday = isSameDay(date, today);
                  return (
                    <button
                      key={i}
                      className={[
                        "atc-week-cell",
                        isWeekend && "is-weekend",
                        isToday && "is-today",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => openCell(date)}
                    >
                      <span className="atc-week-dow">
                        {WEEKDAYS_FULL[date.getDay()]}
                      </span>
                      <span className="atc-week-num">{date.getDate()}</span>
                      {entry && entry.length ? (
                        renderCellContent(entry, date, 3)
                      ) : isWeekend ? (
                        <span className="atc-weekend-label">Weekend</span>
                      ) : (
                        <span className="atc-weekend-label atc-empty">
                          No task
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {!loading && view === "day" && (
              <div className="atc-day-panel">
                <div className="atc-day-hero">
                  <span className="atc-day-date">{selected.getDate()}</span>
                  <div>
                    <p className="atc-day-dow">
                      {WEEKDAYS_FULL[selected.getDay()]}
                    </p>
                    <p className="atc-day-month">
                      {MONTH_NAMES[selected.getMonth()]}{" "}
                      {selected.getFullYear()}
                    </p>
                  </div>
                </div>
                {(() => {
                  const entry = entryFor(selected);
                  const isWeekend =
                    selected.getDay() === 0 || selected.getDay() === 6;

                  if (entry && entry.length) {
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          width: "100%",
                          maxWidth: "420px",
                        }}
                      >
                        {entry.map((t, idx) => (
                          <div
                            key={idx}
                            className="atc-list-row"
                            style={{
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            <span className="atc-list-name">
                              #{t.taskNr} — {t.taskName}
                              {isMultiEmployeeView
                                ? ` (${t.employeeName || t.employeeCode})`
                                : ""}
                            </span>
                            <span
                              style={{
                                fontSize: 12,
                                color: "var(--text-muted)",
                              }}
                            >
                              {WEEKDAYS_FULL[selected.getDay()]},{" "}
                              {MONTH_NAMES[selected.getMonth()]}{" "}
                              {selected.getDate()}, {selected.getFullYear()}{" "}
                              &nbsp;|&nbsp; {t.startDate} → {t.endDate}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return (
                    <div className="atc-weekend-label atc-empty">
                      {isWeekend
                        ? "Weekend — no task required"
                        : "No task assigned"}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ---------- "View all tasks for this day" modal ---------- */}
      {listModalDate && (
        <div className="atc-modal-backdrop" onMouseDown={closeListModal}>
          <div
            className="atc-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="atc-list-modal-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="atc-modal-head">
              <div>
                <h3 id="atc-list-modal-title">
                  {WEEKDAYS_FULL[listModalDate.getDay()]},{" "}
                  {MONTH_NAMES[listModalDate.getMonth()]}{" "}
                  {listModalDate.getDate()}, {listModalDate.getFullYear()}
                </h3>
                <p>
                  {isAdmin && isMultiEmployeeView
                    ? "All employee tasks"
                    : "Tasks assigned on this day"}
                </p>
              </div>
              <button
                className="atc-icon-btn"
                aria-label="Close"
                onClick={closeListModal}
              >
                ×
              </button>
            </div>
            <div className="atc-modal-list-body">
              {(taskDateMap[toKey(listModalDate)] || []).length === 0 && (
                <p className="atc-weekend-label atc-empty">No task assigned</p>
              )}
              {(taskDateMap[toKey(listModalDate)] || []).map((t, idx) => (
                <div
                  key={idx}
                  className="atc-list-row"
                  style={{ flexDirection: "column", alignItems: "flex-start" }}
                >
                  <span className="atc-list-name">
                    #{t.taskNr} — {t.taskName}
                  </span>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {t.startDate} → {t.endDate}
                    {isMultiEmployeeView && (t.employeeName || t.employeeCode)
                      ? ` • ${t.employeeName || t.employeeCode}`
                      : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
